import { useState, useRef, useCallback } from 'react';
import type { ICopilotExternalQueryConfig } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import defaultGameIcon from '@/pages/dashboard/assets/svg/console-game-default.svg';
import ChartDashboardIcon from '@/pages/aigc/assets/svg/chart-dashboard.svg';
import type { ISelectedChartTagData } from '../components/SelectedChartTag';
import type { IChartPopoverRef } from '../index';

const FORM_CUSTOM_DASHBOARD = 'custom_dashboard';

const buildExternalChartPlaceholder = (
  gameCode: string,
  dashboardId: string,
): ISelectedChartTagData => ({
  chartId: dashboardId,
  uniqueKey: `__chart_loading__:${gameCode}:${dashboardId}`,
  nameEN: '-',
  nameCN: '-',
  gameIcon: defaultGameIcon,
  chartTypeIcon: ChartDashboardIcon,
  gameName: gameCode,
  type: 'dashboard',
  gameCode,
  realDashboardId: dashboardId,
  isLoading: true,
});

const resolveInitialCharts = (
  externalQueryConfig?: ICopilotExternalQueryConfig | null,
): ISelectedChartTagData[] => {
  if (
    externalQueryConfig?.form === FORM_CUSTOM_DASHBOARD
    && externalQueryConfig.gameCode
    && externalQueryConfig.dashboardId
  ) {
    return [
      buildExternalChartPlaceholder(
        externalQueryConfig.gameCode,
        externalQueryConfig.dashboardId,
      ),
    ];
  }
  return [];
};

/** 管理 chart tag 的状态与回调（选中/删除/回点/清空） */
const useChartTags = (externalQueryConfig?: ICopilotExternalQueryConfig | null) => {
  const [selectedCharts, setSelectedCharts] = useState<ISelectedChartTagData[]>(() =>
    resolveInitialCharts(externalQueryConfig),
  );
  const chartPopoverRef = useRef<IChartPopoverRef>(null);

  /** 面板确认：按 gameCode 整体替换当前游戏的图表，保留其他游戏 */
  const handleChartConfirm = useCallback(
    (newCharts: ISelectedChartTagData[], gameCode: string) => {
      setSelectedCharts((prev) => {
        const otherGameCharts = prev.filter((c) => c.gameCode !== gameCode);
        return newCharts.length === 0 ? otherGameCharts : [...otherGameCharts, ...newCharts];
      });
    },
    [],
  );

  /** 按 uniqueKey 移除单个标签 */
  const handleRemoveChartTag = useCallback(
    (uniqueKey: string) => {
      setSelectedCharts((prev) => prev.filter((c) => c.uniqueKey !== uniqueKey));
    },
    [],
  );

  /** 点击标签箭头：打开浮窗并高亮对应 dashboard，不改动选中态 */
  const handleChartTagClick = useCallback(
    (chart: ISelectedChartTagData) => {
      chartPopoverRef.current?.open(chart.gameCode, chart.dashboardValue);
    },
    [],
  );

  /** 清空全部 chart tag（发送成功后调用） */
  const handleClearCharts = useCallback(() => {
    setSelectedCharts([]);
  }, []);

  return {
    selectedCharts,
    setSelectedCharts,
    chartPopoverRef,
    handleChartConfirm,
    handleRemoveChartTag,
    handleChartTagClick,
    handleClearCharts,
  };
};

export default useChartTags;
